/**
 * Shadows @plone/volto Header. De-branded chrome for the three archives: the
 * affiliation notice strip, the full Doxcelerate wordmark, and a prominent
 * search box on the right. The primary navigation renders from the active site
 * config (siteConfig.nav). All DOE / energy.gov logos and language removed.
 *
 * The archive's own name used to sit under the wordmark as a tagline, and a
 * comment here claimed that made "not a DOE site" read before anything else on
 * the page. It read "archives of <site title>" and made no such claim. The name
 * now appears inside the notice's own sentence, which does (BL-0114).
 *
 * Emits the design system's exact `prc-*` DOM (Masthead + PrimaryNav —
 * CONTRACT C of the faithful design port). The `.prc-masthead`/`.prc-nav`
 * CSS lives in the globally-imported ds-core.css; this component owns only
 * the markup + data wiring.
 *
 * No login link. Search is the one thing a visitor comes to the masthead for,
 * and every pixel the login took is a pixel the search box did not have.
 * Editors reach the login form at /login directly.
 *
 * Nav is a flat list — `siteConfig/types.ts` NavLink has no `children` field
 * and no shipped site config nests items, so the design's "Documents"
 * dropdown pattern is intentionally NOT implemented here. Revisit if/when a
 * site config introduces nested nav.
 */
import React from 'react';
import cx from 'classnames';
import { NavLink, Link, useHistory, useLocation } from 'react-router-dom';
import AffiliationNotice from '../../../../components/Layout/AffiliationNotice';
import { Wordmark } from '../../../../components/Layout/Brand';
import { useSiteConfig } from '../../../../helpers/useSiteConfig';

// Magnifier glyph (inline so the masthead search needs no icon import).
const SearchGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true" fill="none">
    <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
    <line x1="13.5" y1="13.5" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/**
 * Masthead search box. Routes to the Search App (/search) with the term as a
 * URL param — the search block seeds its query from ?SearchableText= (Volto's
 * withSearch reads locationSearchData.SearchableText, which
 * helpers/useCanonicalSearchUrl then folds into the block's own query).
 */
const MastheadSearch = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const [q, setQ] = React.useState('');
  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    history.push(
      term ? `/search?SearchableText=${encodeURIComponent(term)}` : '/search',
    );
  };
  // One search box at all times: on the Search App route the in-block
  // SearchInput is the single source of truth, and the home page carries its
  // own central search block, so suppress the masthead box on both (they
  // otherwise desync until a page refresh). This test sits
  // BELOW every hook — submitting the box navigates to /search, and an early
  // return above useState would drop a hook between those two renders.
  const path = (pathname || '').replace(/\/+$/, '');
  if (path === '' || path === '/search' || path.endsWith('/search')) return null;
  return (
    <form className="prc-masthead__search" role="search" onSubmit={submit}>
      <div className="prc-masthead__search-field">
        <input
          type="search"
          name="SearchableText"
          aria-label="Search documents"
          placeholder="Search documents…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit" aria-label="Search">
          <SearchGlyph />
        </button>
      </div>
    </form>
  );
};

const Header = () => {
  const site = useSiteConfig();

  // item 31: .prc-nav__inner scrolls (overflow-x:auto) with its scrollbar
  // deliberately hidden, so on a phone -- where the link row never fits --
  // there was no affordance at all telling a visitor Help/Contact exist
  // off-screen. Toggle edge-fade classes from real scroll position rather
  // than a width guess, so the same code is correct at any nav length.
  const navRef = React.useRef(null);
  const [navScroll, setNavScroll] = React.useState({ start: true, end: true });
  React.useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const update = () => {
      setNavScroll({
        start: el.scrollLeft <= 1,
        end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
      });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [site.nav]);

  return (
    <>
      <AffiliationNotice />
      <header className="prc-masthead">
        {/* __inner centers the brand + search to the same `container-max`
            column as the nav/breadcrumb/main/footer (the design bundle
            omitted this wrapper, misaligning the header at wide viewports). */}
        <div className="prc-masthead__inner">
          <div className="prc-masthead__brand">
            <Link to="/" className="prc-masthead__logo-link" title={site.title}>
              <Wordmark className="prc-masthead__wordmark" />
            </Link>
          </div>
          {/* Solr-backed Search App entry point (see theme/Search/Search.jsx). */}
          <MastheadSearch />
        </div>
      </header>
      <nav
        id="navigation"
        className={cx('prc-nav', {
          'has-more-start': !navScroll.start,
          'has-more-end': !navScroll.end,
        })}
        aria-label="Primary"
      >
        <div className="prc-nav__inner" ref={navRef}>
          {site.nav.map((item) =>
            item.external ? (
              <a
                key={item.to}
                href={item.to}
                className="prc-nav__link"
                rel="noopener noreferrer"
              >
                {item.label}
              </a>
            ) : (
              // NavLink (react-router-dom v5) sets `aria-current="page"` on
              // the active link by default — that's the design's active
              // state selector. `activeClassName=""` suppresses the default
              // `.active` class since the design doesn't use it.
              <NavLink
                key={item.to}
                to={item.to}
                exact={item.to === '/'}
                className="prc-nav__link"
                activeClassName=""
              >
                {item.label}
              </NavLink>
            ),
          )}
        </div>
      </nav>
    </>
  );
};

export default Header;
